import React, { useState } from 'react';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { Card, CardContent, Typography, CircularProgress, Grid, Alert, Box, IconButton, Divider } from '@mui/material';
import { useResourceParams } from '@refinedev/core';

import { useMitreMitigations } from '../../../../hooks/useMitreMitigations';

const MAX_DESC_LENGTH = 130;

const ScenarioMitreMitigations: React.FC = () => {
  const { id: scenarioId } = useResourceParams();
  const { data = [], isLoading, error } = useMitreMitigations(scenarioId?.toString());
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading MITRE mitigations...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading mitigations.</Alert>;
  }

  if (!data.length) {
    return <Alert severity="info">No MITRE mitigations found for this scenario.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        MITRE Mitigations
      </Typography>
      <Grid container spacing={3}>
        {data.map(mitigation => {
          const isExpanded = expandedId === mitigation.id;
          const isTruncated = mitigation.description && mitigation.description.length > MAX_DESC_LENGTH;
          const displayDesc =
            isExpanded || !isTruncated
              ? mitigation.description
              : mitigation.description?.slice(0, MAX_DESC_LENGTH) + '...';

          return (
            <Grid item xs={12} md={6} lg={4} key={mitigation.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  minHeight: 212,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.07)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.13)' }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1.2}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ fontWeight: 700, fontSize: 16, letterSpacing: 0.5, mr: 1.5 }}
                    >
                      {mitigation.mitigation_id}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        flex: 1,
                        fontWeight: 600,
                        fontSize: 18,
                        lineHeight: 1.2,
                        color: 'text.primary'
                      }}
                    >
                      {mitigation.name}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1 }} />

                  <Box display="flex" alignItems="center" mb={0.7}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>
                      Technique:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      {mitigation.technique_id}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>
                      Version:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      {mitigation.mitre_attack_version}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="flex-start" mt={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mr: 1, minWidth: 82, mt: 0.4, flexShrink: 0 }}
                    >
                      Description:
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 40 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.98rem',
                          color: 'text.primary',
                          lineHeight: 1.55,
                          wordBreak: 'break-word',
                          cursor: isTruncated ? 'pointer' : 'default'
                        }}
                      >
                        {displayDesc}
                      </Typography>
                    </Box>
                    {isTruncated && (
                      <IconButton
                        size="small"
                        aria-label={isExpanded ? 'Show less' : 'Show more'}
                        onClick={() => setExpandedId(isExpanded ? null : mitigation.id)}
                        sx={{ ml: 0.5, mt: 0.5 }}
                      >
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ScenarioMitreMitigations;
